import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import Footer from 'src/components/Footer';
import { Box, Card, Container, Grid, Tab, Tabs } from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { TabsContainerWrapper } from 'src/layouts/TabsContainerWrapper';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import TreeView from '../Tree/treeView';
import routes from 'src/Resources/routes.json';
import InstallationSearch from './InstallationSearch';
import { FormattedMessage } from 'react-intl';
import { UserContext } from '../../../contexts/userContext';
import { InstallationsContext } from '../../../contexts/InstallationsContextProvider';
import Installation from './Installation';
import { UserType } from '../../../interfaces/UserTypes';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';

interface InstallationTabsProps {
  product: number;
}

function InstallationTabs(props: InstallationTabsProps) {
  const location = useLocation();
  const context = useContext(UserContext);
  const { currentUser } = context;
  const tabList = [
    'live',
    'overview',
    // 'manage',
    'batteryview',
    'log',
    'information',
    'configuration',
    'history',
    'pvview',
    'installationTickets',
    'documents',
    'checklist'
  ];

  const [currentTab, setCurrentTab] = useState<string>(undefined);

  const {
    salimax_or_sodistore_Installations,
    sodistoreGridInstallations,
    fetchAllInstallations,
    socket,
    openSocket,
    closeSocket
  } = useContext(InstallationsContext);

  // Use the correct installations array based on product
  const installations =
    props.product === 4
      ? sodistoreGridInstallations
      : salimax_or_sodistore_Installations;
  const { product, setProduct } = useContext(ProductIdContext);

  useEffect(() => {
    let path = location.pathname.split('/');

    if (path[path.length - 2] === 'list') {
      setCurrentTab('list');
      fetchAllInstallations(product, false);
    } else if (path[path.length - 2] === 'tree') {
      setCurrentTab('tree');
    } else {
      //Even if we are located at path: /batteryview/mainstats, we want the BatteryViewSalidomo tab to be bold
      setCurrentTab(path.find((pathElement) => tabList.includes(pathElement)));
    }
  }, [location]);

  useEffect(() => {
    setProduct(props.product);
  }, [props.product]);

  useEffect(() => {
    if (product == props.product) {
      fetchAllInstallations(product);
    }
  }, [product]);

  const handleTabsChange = (_event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };

  const navigateToTabPath = (pathname: string, tab_value: string): string => {
    let pathlist = pathname.split('/');
    let ret_path = '';
    for (let i = 1; i < pathlist.length; i++) {
      if (Number.isNaN(Number(pathlist[i]))) {
        ret_path += '/';
        ret_path += pathlist[i];
      } else {
        ret_path += '/';
        ret_path += pathlist[i];
        ret_path += '/';
        break;
      }
    }

    ret_path += tab_value;
    return ret_path;
  };

  // TODO: SodistoreGrid — PV View excluded for product 4, add back when data path is ready
  const hidePvView = props.product === 4;

  // Checklist is only shown for Sodistore Grid (product=4) in the Installations view.
  // Salimax (0) / Salidomo (1) / SodistoreMax (3) use different onboarding flows.
  const showChecklist = props.product === 4;

  const singleInstallationTabs = (
    currentUser.userType == UserType.admin
      ? [
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },

          // {
          //   value: 'manage',
          //   label: (
          //     <FormattedMessage
          //       id="manage"
          //       defaultMessage="Access Management"
          //     />
          //   )
          // },
          {
            value: 'log',
            label: <FormattedMessage id="log" defaultMessage="Log" />
          },
          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },

          {
            value: 'configuration',
            label: (
              <FormattedMessage
                id="configuration"
                defaultMessage="Configuration"
              />
            )
          },
          {
            value: 'history',
            label: (
              <FormattedMessage
                id="history"
                defaultMessage="History Of Actions"
              />
            )
          },
          {
            value: 'pvview',
            label: <FormattedMessage id="pvview" defaultMessage="Pv View" />
          },
          {
            value: 'installationTickets',
            label: <FormattedMessage id="tickets" defaultMessage="Tickets" />
          },
          {
            value: 'documents',
            label: <FormattedMessage id="documentsTab" defaultMessage="Documents" />
          },
          {
            value: 'checklist',
            label: <FormattedMessage id="checklist" defaultMessage="Checklist" />
          }
        ]
      : currentUser.userType == UserType.partner
      ? [
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          {
            value: 'pvview',
            label: <FormattedMessage id="pvview" defaultMessage="Pv View" />
          },

          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          {
            value: 'documents',
            label: <FormattedMessage id="documentsTab" defaultMessage="Documents" />
          }
        ]
      : [
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },

          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          }
        ]
  )
    .filter((tab) => !(hidePvView && tab.value === 'pvview'))
    .filter((tab) => !(!showChecklist && tab.value === 'checklist'));

  const tabs =
    currentTab != 'list' &&
    currentTab != 'tree' &&
    !location.pathname.includes('folder')
      ? currentUser.userType == UserType.admin
        ? [
            {
              value: 'list',
              icon: <ListIcon id="mode-toggle-button-list-icon" />
            },
            {
              value: 'tree',
              icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
            },
            {
              value: 'live',
              label: <FormattedMessage id="live" defaultMessage="Live" />
            },
            {
              value: 'overview',
              label: (
                <FormattedMessage id="overview" defaultMessage="Overview" />
              )
            },
            {
              value: 'batteryview',
              label: (
                <FormattedMessage
                  id="batteryview"
                  defaultMessage="Battery View"
                />
              )
            },
            {
              value: 'pvview',
              label: <FormattedMessage id="pvview" defaultMessage="Pv View" />
            },
            // {
            //   value: 'manage',
            //   label: (
            //     <FormattedMessage
            //       id="manage"
            //       defaultMessage="Access Management"
            //     />
            //   )
            // },
            {
              value: 'log',
              label: <FormattedMessage id="log" defaultMessage="Log" />
            },
            {
              value: 'information',
              label: (
                <FormattedMessage
                  id="information"
                  defaultMessage="Information"
                />
              )
            },

            {
              value: 'configuration',
              label: (
                <FormattedMessage
                  id="configuration"
                  defaultMessage="Configuration"
                />
              )
            },
            {
              value: 'history',
              label: (
                <FormattedMessage
                  id="history"
                  defaultMessage="History Of Actions"
                />
              )
            },
            {
              value: 'installationTickets',
              label: <FormattedMessage id="tickets" defaultMessage="Tickets" />
            },
            {
              value: 'documents',
              label: <FormattedMessage id="documentsTab" defaultMessage="Documents" />
            },
            {
              value: 'checklist',
              label: <FormattedMessage id="checklist" defaultMessage="Checklist" />
            }
          ]
        : currentUser.userType == UserType.partner
        ? [
            {
              value: 'list',
              icon: <ListIcon id="mode-toggle-button-list-icon" />
            },
            {
              value: 'tree',
              icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
            },

            {
              value: 'live',
              label: <FormattedMessage id="live" defaultMessage="Live" />
            },
            {
              value: 'overview',
              label: (
                <FormattedMessage id="overview" defaultMessage="Overview" />
              )
            },
            {
              value: 'batteryview',
              label: (
                <FormattedMessage
                  id="batteryview"
                  defaultMessage="Battery View"
                />
              )
            },
            {
              value: 'pvview',
              label: <FormattedMessage id="pvview" defaultMessage="Pv View" />
            },

            {
              value: 'information',
              label: (
                <FormattedMessage
                  id="information"
                  defaultMessage="Information"
                />
              )
            },
            {
              value: 'documents',
              label: <FormattedMessage id="documentsTab" defaultMessage="Documents" />
            }
          ]
        : [
            {
              value: 'list',
              icon: <ListIcon id="mode-toggle-button-list-icon" />
            },
            {
              value: 'tree',
              icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
            },

            {
              value: 'live',
              label: <FormattedMessage id="live" defaultMessage="Live" />
            },
            {
              value: 'overview',
              label: (
                <FormattedMessage id="overview" defaultMessage="Overview" />
              )
            },

            {
              value: 'information',
              label: (
                <FormattedMessage
                  id="information"
                  defaultMessage="Information"
                />
              )
            }
          ]
      : [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          }
        ];

  // Filter out PV View for SodistoreGrid
  const filteredTabs = (hidePvView
    ? tabs.filter((tab) => tab.value !== 'pvview')
    : tabs
  ).filter((tab) => !(!showChecklist && tab.value === 'checklist'));

  return installations.length > 1 ? (
    <>
      <Container maxWidth="xl" sx={{ marginTop: '20px' }} className="mainframe">
        <TabsContainerWrapper>
          <Tabs
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {filteredTabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                icon={tab.icon}
                component={Link}
                label={tab.label}
                to={
                  tab.value === 'list' || tab.value === 'tree'
                    ? routes[tab.value]
                    : navigateToTabPath(location.pathname, routes[tab.value])
                }
                id={`tour-tab-${tab.value}`}
              />
            ))}
          </Tabs>
        </TabsContainerWrapper>
        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={routes.list + '*'}
                element={
                  <Grid item xs={12}>
                    <Box p={4}>
                      <InstallationSearch
                        installations={installations}
                      />
                    </Box>
                  </Grid>
                }
              />

              <Route path={routes.tree + '*'} element={<TreeView />} />
              <Route
                path={'*'}
                element={
                  props.product === 0 ? (
                    <Navigate to={routes.installations + routes.list} />
                  ) : props.product === 4 ? (
                    <Navigate
                      to={routes.sodistoregrid_installations + routes.list}
                    />
                  ) : props.product === 5 ? (
                    <Navigate
                      to={routes.sodistorepro_installations + routes.list}
                    />
                  ) : (
                    <Navigate
                      to={routes.sodistore_installations + routes.list}
                    />
                  )
                }
              ></Route>
            </Routes>
          </Grid>
        </Card>
      </Container>
      <Footer />
    </>
  ) : installations.length === 1 ? (
    <>
      <Container maxWidth="xl" sx={{ marginTop: '20px' }} className="mainframe">
        <TabsContainerWrapper>
          <Tabs
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {singleInstallationTabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                component={Link}
                label={tab.label}
                to={routes[tab.value]}
                id={`tour-tab-${tab.value}`}
              />
            ))}
          </Tabs>
        </TabsContainerWrapper>
        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={'*'}
                element={
                  <Grid item xs={12}>
                    <Box p={4}>
                      <Installation
                        current_installation={
                          installations[0]
                        }
                        type="installation"
                      ></Installation>
                    </Box>
                  </Grid>
                }
              />
            </Routes>
          </Grid>
        </Card>
      </Container>
      <Footer />
    </>
  ) : null;
}

export default InstallationTabs;
